import { RefObject, useEffect, useMemo, useState } from 'react';
import { fetchLeadActivities } from '@/services/api/lead';
import { processAPIResponseNow } from '@/utils/request';
import { LeadActivityModel } from '@/typings/models/LeadActivity';
import { PaginationLinksModel } from '@/typings/models/PaginationLinks';
import { useInfiniteScrollPagination } from '@/hooks/useInfiniteScrollPagination';

interface UseLeadActivitiesOptions {
  leadId: string;
  pageSize?: number;
  containerRef?: RefObject<HTMLDivElement | null>;
}

interface UseLeadActivitiesResult {
  activities: LeadActivityModel[];
  comments: LeadActivityModel[];
  loading: boolean;
  paginationLoading: boolean;
  hasMore: boolean;
  refresh: () => void;
}

export function useLeadActivities({
  leadId,
  pageSize = 100,
  containerRef,
}: UseLeadActivitiesOptions): UseLeadActivitiesResult {
  const [activities, setActivities] = useState<LeadActivityModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [links, setLinks] = useState<Partial<PaginationLinksModel> | null>(null);

  const load = (url: string | null, append: boolean) => {
    (append ? setPaginationLoading : setLoading)(true);

    fetchLeadActivities({
      params: { leadId },
      url,
      query: { page: 1, page_size: pageSize },
    }).then(APIResponse => {
      processAPIResponseNow(APIResponse, {
        onSuccess(response) {
          const incoming = response.data || [];
          setActivities(prev => (append ? [...prev, ...incoming] : incoming));
          setLinks(response.links || null);
        },
        onEnd() {
          (append ? setPaginationLoading : setLoading)(false);
        },
      });
    });
  };

  const refresh = () => {
    setLinks(null);
    load(null, false);
  };

  useEffect(() => {
    if (leadId) refresh();
  }, [leadId]);

  useInfiniteScrollPagination({
    container: containerRef,
    enabled: Boolean(containerRef),
    loading: loading || paginationLoading,
    canLoadMore: Boolean(links?.next),
    links,
    onLoadMore: () => load(links?.next ?? null, true),
  });

  const comments = useMemo(
    () => activities.filter(a => a.activity_type === 'comment'),
    [activities],
  );

  return { activities, comments, loading, paginationLoading, hasMore: Boolean(links?.next), refresh };
}
